import { useEffect, useMemo, useRef, useState } from 'react';

import type { StickyNote } from '@backrs_ui/common-ui';

import {
  clampToBounds,
  type Position,
} from '@backrs_ui/grow-ui/src/components/StickyNotes/hooks/useDraggable';
import { StickyNoteCard } from '@backrs_ui/grow-ui/src/components/StickyNotes/StickyNoteCard';
import {
  STICKY_NOTE_DECK_STEP,
  STICKY_NOTE_HEIGHT,
  STICKY_NOTE_WIDTH,
} from '@backrs_ui/grow-ui/src/components/StickyNotes/stickyNotes.constants';
import { StickyNoteStarter } from '@backrs_ui/grow-ui/src/components/StickyNotes/StickyNoteStarter';
import { selectIsEmulating } from '@backrs_ui/grow-ui/src/redux/emulator/emulator';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useCreateStickyNoteMutation,
  useGetStickyNotesQuery,
  useUpdateStickyNoteMutation,
} from '@backrs_ui/grow-ui/src/redux/stickyNotes/stickyNotes.api';
import { removePristineId } from '@backrs_ui/grow-ui/src/redux/stickyNotes/stickyNotes.slice';
import type { StickyNoteTarget } from '@backrs_ui/grow-ui/src/redux/stickyNotes/stickyNotes.types';

import { Box, SxProps, Theme, useMediaQuery } from '@mui/material';

const NOTE_BOX = { width: STICKY_NOTE_WIDTH, height: STICKY_NOTE_HEIGHT };

type Size = Readonly<{ width: number; height: number }>;

type StickyNoteBoardProps = Readonly<{
  target: StickyNoteTarget;
}>;

/**
 * Positions are pixels from the top-left of the nearest positioned ancestor, which the layer covers.
 * Below the stack breakpoint the layer is not rendered and notes list in normal flow;
 * saved positions are left untouched.
 */
export const StickyNoteBoard = ({ target }: StickyNoteBoardProps) => {
  const dispatch = useAppDispatch();
  const readOnly = useAppSelector(selectIsEmulating);

  const { currentData: notes } = useGetStickyNotesQuery(target);
  const [createStickyNote] = useCreateStickyNoteMutation();
  const [updateStickyNote] = useUpdateStickyNoteMutation();

  const rowRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const isCreatingRef = useRef(false);
  // Text typed into the starter while its note is being created, handed to the new card.
  const starterTextRef = useRef('');
  const [starterNoteId, setStarterNoteId] = useState<number | null>(null);
  const [hasStarterError, setHasStarterError] = useState(false);
  const [layerSize, setLayerSize] = useState<Size | null>(null);
  const [raisedNoteId, setRaisedNoteId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  // Follows the viewport, not the page width, so a wide sidebar on desktop keeps notes where
  // they were placed.
  const isStacked = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const noteCount = notes?.length ?? 0;
  const showStarter = Boolean(notes) && noteCount === 0 && !readOnly;

  const stackedNotes = useMemo(
    () => (notes ? [...notes].sort((a, b) => a.id - b.id) : []),
    [notes],
  );

  // ResizeObserver doesn't deliver in a hidden tab, so the observer seeds from layout first.
  // The layer unmounts in stack mode, so its observer re-attaches whenever it comes back.
  useEffect(() => {
    const layer = layerRef.current;

    if (!layer) {
      setLayerSize(null);

      return;
    }

    setLayerSize({ width: layer.clientWidth, height: layer.clientHeight });

    const observer = new ResizeObserver(([entry]) => {
      setLayerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });

    observer.observe(layer);

    return () => observer.disconnect();
  }, [isStacked]);

  // The seed has been handed over once the card renders; a later remount must not reapply it.
  useEffect(() => {
    if (starterNoteId !== null && notes?.some((note) => note.id === starterNoteId)) {
      setStarterNoteId(null);
    }
  }, [notes, starterNoteId]);

  // Measured against the row's positioned ancestor, which the layer covers, so it works in both modes.
  const getDeckPosition = () => {
    const row = rowRef.current;
    const board = row?.offsetParent;

    if (!row || !board) return null;

    const boardRect = board.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();

    return {
      position_x: Math.round(rowRect.left - boardRect.left),
      position_y: Math.round(rowRect.top - boardRect.top + noteCount * STICKY_NOTE_DECK_STEP),
    };
  };

  const createNote = (text?: string) => {
    const position = getDeckPosition();

    if (!position || isCreatingRef.current) return null;

    isCreatingRef.current = true;

    const request = createStickyNote({ ...target, color: 'color_1', text, ...position });

    request
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        isCreatingRef.current = false;
      });

    return request;
  };

  // The first non-empty keystroke saves the starter as a real note.
  const handleStarterChange = (text: string) => {
    starterTextRef.current = text;

    if (!text) return;

    createNote(text)
      ?.unwrap()
      .then((created) => {
        setHasStarterError(false);
        setStarterNoteId(created.id);
      })
      .catch(() => setHasStarterError(true));
  };

  const handleCreate = async () => {
    await createNote()?.unwrap();
  };

  const seedTextFor = (note: StickyNote) =>
    note.id === starterNoteId ? starterTextRef.current : undefined;

  const handleDragEnd = (note: StickyNote) => (position: Position) => {
    setDraggingId(null);
    dispatch(removePristineId(note.id));
    updateStickyNote({
      ...target,
      id: note.id,
      position_x: Math.round(position.x),
      position_y: Math.round(position.y),
    });
  };

  return (
    <>
      <Box ref={rowRef} id="sticky-note-row" sx={rowSx(isStacked)}>
        {showStarter && (
          <Box sx={starterSlotSx}>
            <StickyNoteStarter onChange={handleStarterChange} hasError={hasStarterError} />
          </Box>
        )}

        {isStacked && noteCount > 0 && (
          <Box id="sticky-note-stack" sx={stackSx}>
            {stackedNotes.map((note) => (
              <StickyNoteCard
                key={note.id}
                note={note}
                target={target}
                readOnly={readOnly}
                onCreate={handleCreate}
                seedText={seedTextFor(note)}
              />
            ))}
          </Box>
        )}

        {!isStacked && noteCount > 0 && <Box sx={deckSpacerSx(noteCount)} />}
      </Box>

      {!isStacked && (
        <Box ref={layerRef} id="sticky-note-layer" sx={layerSx}>
          {layerSize &&
            notes?.map((note) => (
              <StickyNoteCard
                key={note.id}
                note={note}
                target={target}
                readOnly={readOnly}
                onCreate={handleCreate}
                seedText={seedTextFor(note)}
                placement={{
                  position: clampToBounds(
                    { x: note.position_x, y: note.position_y },
                    layerSize,
                    NOTE_BOX,
                  ),
                  boundsRef: layerRef,
                  isDragging: draggingId === note.id,
                  isRaised: raisedNoteId === note.id,
                  onDragStart: () => {
                    setDraggingId(note.id);
                    setRaisedNoteId(note.id);
                  },
                  onDragEnd: handleDragEnd(note),
                }}
              />
            ))}
        </Box>
      )}
    </>
  );
};

const rowSx = (isStacked: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  ...(isStacked ? { width: '100%' } : { flex: 1, minWidth: STICKY_NOTE_WIDTH }),
});

// Holds room for the deck so spawned notes never clamp up over the content above.
const deckSpacerSx = (count: number): SxProps<Theme> => ({
  flexShrink: 0,
  height: STICKY_NOTE_HEIGHT + STICKY_NOTE_DECK_STEP * (count - 1),
});

// Sits where the first deck note spawns, so the saved note replaces it in place.
const starterSlotSx: SxProps<Theme> = {
  width: STICKY_NOTE_WIDTH,
  maxWidth: '100%',
};

const stackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
  mt: backrs.spacing['spacing-lg'],
});

const layerSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
};
